import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteButton from './DeleteButton';
import {formatDateAgo} from '../../utils/dateUtils';
import {sanitizeHtml} from '../../utils/sanitize';
import {GiphyFetch} from "@giphy/js-fetch-api";
import {EmojiVariationsList} from "@giphy/react-components";
import PostCommentComponent from "./PostCommentComponent";
import useTranslation from "../../customHooks/translations";
import {DEFAULT_GRADIENT, DEFAULT_THEME_COLOR} from "../../constants/theme";

const gf = new GiphyFetch('sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh');

const AuthorRow = ({ author, createdAt, show, slug, commentId, replySlug, replyId, translation }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
    <Link to={`/@${author.username}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
      <Avatar
        src={author.imageUrl}
        alt={author.username}
        sx={{ width: 32, height: 32, border: '1px solid rgba(255,255,255,0.1)' }}
      />
    </Link>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Link to={`/@${author.username}`} style={{ textDecoration: 'none' }}>
        <Typography
          variant="caption"
          sx={{
            background: DEFAULT_GRADIENT,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
          }}
        >
          {author.username}
        </Typography>
      </Link>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', fontSize: '0.7rem' }}>
        {formatDateAgo(createdAt)}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {replySlug && (
        <PostCommentComponent
          slug={replySlug}
          text={<ReplyIcon sx={{ fontSize: 16 }} />}
          replyTo={replyId}
          compact
        />
      )}
      <DeleteButton show={show} slug={slug} commentId={commentId} />
    </Box>
  </Box>
);

const CommentBody = ({ comment }) => (
  <Box
    sx={{
      color: 'rgba(255,255,255,0.8)',
      fontSize: '0.9rem',
      lineHeight: 1.55,
      '& p': { margin: 0 },
      '& a': { color: DEFAULT_THEME_COLOR },
    }}
  >
    {comment.gifId
      ? <EmojiVariationsList noLink hideAttribution fetchVariations={(id) => gf.emojiVariations(id)} gif={comment.gif} gifHeight={100} />
      : <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
    }
  </Box>
);

const Comment = props => {
  const translation = useTranslation();
  const isOwner = props.currentUser && props.currentUser.username === props.comment.author.username;
  const [comment, setComment] = useState(null);

  useEffect(() => {
    const prepareComments = async () => {
      let preparedComment = { ...props.comment };
      if (preparedComment.gifId) {
        const res = await gf.gif(props.comment.gifId);
        preparedComment.gif = res.data;
      }
      if (props.comment.replies) {
        preparedComment.replies = [];
        for (const reply of props.comment.replies) {
          let r = { ...reply };
          if (reply.gifId) {
            const res = await gf.gif(reply.gifId);
            r.gif = res.data;
          }
          preparedComment.replies.push(r);
        }
      }
      setComment(preparedComment);
    };
    prepareComments();
  }, [props.comment]);

  if (!comment || comment.parent) return null;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '12px',
        p: 2,
        mb: 1.5,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.35)' },
      }}
    >
      <CommentBody comment={comment} />
      <AuthorRow
        author={comment.author}
        createdAt={comment.createdAt}
        show={isOwner}
        slug={props.slug}
        commentId={comment.id}
        replySlug={props.slug}
        replyId={comment.id}
        translation={translation}
      />

      {comment.replies?.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            pl: 2,
            borderLeft: '2px solid rgba(54,201,162,0.25)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {comment.replies.map(reply => {
            const replyIsOwner = props.currentUser && props.currentUser.username === reply.author.username;
            return (
              <Box key={reply.id}>
                <CommentBody comment={reply} />
                <AuthorRow
                  author={reply.author}
                  createdAt={reply.createdAt}
                  show={replyIsOwner}
                  slug={props.slug}
                  commentId={reply.id}
                  translation={translation}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Comment;
